import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Property } from '../services/propertyApi';

interface PropertyContextType {
  properties: Property[];
  setProperties: (properties: Property[]) => void;
  addProperty: (property: Property) => void;
  updateProperty: (id: string, property: Partial<Property>) => void;
  removeProperty: (id: string) => void;
  refreshProperties: () => void;
  lastUpdated: Date | null;
}

const PropertyContext = createContext<PropertyContextType | undefined>(undefined);

export const usePropertyContext = () => {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('usePropertyContext must be used within a PropertyProvider');
  }
  return context;
};

interface PropertyProviderProps {
  children: ReactNode;
}

export const PropertyProvider: React.FC<PropertyProviderProps> = ({ children }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const addProperty = useCallback((property: any) => {
    // Transform property to include proper image handling
    const transformedProperty = {
      ...property,
      id: property.id.toString(),
      images: (() => {
        const imgs: string[] = [];
        if (property.main_image) imgs.push(property.main_image);
        if (property.gallery_images?.length) {
          property.gallery_images.forEach((img: string) => {
            if (img && !imgs.includes(img)) imgs.push(img);
          });
        }
        return imgs.length ? imgs : [];
      })(),
      location: property.full_address || '',
    };
    setProperties(prev => [transformedProperty, ...prev]);
    setLastUpdated(new Date());
  }, []);

  const updateProperty = useCallback((id: string, updatedProperty: Partial<Property>) => {
    setProperties(prev => 
      prev.map(prop => 
        prop.id.toString() === id 
          ? { ...prop, ...updatedProperty }
          : prop
      )
    );
    setLastUpdated(new Date());
  }, []);

  const removeProperty = useCallback((id: string) => {
    setProperties(prev => prev.filter(prop => prop.id.toString() !== id));
    setLastUpdated(new Date());
  }, []);

  const refreshProperties = useCallback(() => {
    setLastUpdated(new Date());
  }, []);

  const value: PropertyContextType = {
    properties,
    setProperties,
    addProperty,
    updateProperty,
    removeProperty,
    refreshProperties,
    lastUpdated
  };

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
};
